// The kitchen display and its ticket states (F-FNB-04; spec/13 §6.2, spec/06 §8.1), and a ticket's
// printing and routing (F-FNB-05; spec/13 §6.3, §11).
//
//   GET  …/venues/{v}/kitchen/tickets            the tickets on a venue's preparation stations
//   POST …/venues/{v}/kitchen/tickets/{id}/state advance one through its states, or recall it
//   POST …/venues/{v}/kitchen/tickets/{id}/print print (or reprint) one as a print job, and say where it routed
//
// A **kitchen ticket** (KOT) is what one preparation station — the kitchen or the bar — makes for one
// order. The `kitchen` module owns it and builds it from the `food` module's `food.order.placed`
// event through the outbox, never by reaching into another module's tables (spec/03 §5). The states
// are the spec's: `new` → `accepted` → `preparing` → `ready` → `delivered`, with `cancelled` when the
// order is; a **recall** steps one back, and the last 10 delivered tickets can be recalled (spec/13 §6.2).
//
// The live push to the kitchen display and the player's kiosk/app tracker (spec/12 §7.6) is wired
// here as the `kitchen.ticket.*` realtime message on `venue:{v}:kitchen` (`../realtime/kitchen.ts`):
// the ticket's changes publish those as domain events, and the gateway fans them out (spec/09 §6.3).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { FoodOrderIdSchema, KitchenTicketIdSchema, KitchenTicketItemIdSchema, SaleIdSchema, StationIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { FoodPreparationAreaSchema } from "./food.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { KITCHEN_READ, KITCHEN_UPDATE } from "../permissions/index.js";
import { PrintJobSchema } from "./printers.js";
/**
 * Where a ticket is in its lifecycle (spec/13 §6.2, F-FNB-04): `new → accepted → preparing → ready → delivered`,
 * or `cancelled`. The display labels the moves Accept, Preparing, Ready, Delivered.
 */
export const KitchenTicketStateSchema = z
    .enum(['new', 'accepted', 'preparing', 'ready', 'delivered', 'cancelled'])
    .openapi('KitchenTicketState', { description: 'A kitchen ticket’s state (spec/13 §6.2).' });
/** The area a ticket is for. A kitchen ticket is only ever `kitchen` or `bar` — `none` needs no ticket. */
export const KitchenTicketAreaSchema = z
    .enum(['kitchen', 'bar'])
    .openapi('KitchenTicketArea', { description: 'The preparation station a ticket is on (spec/13 §6.2).' });
/** One item on a ticket, a snapshot of the order line it came from (spec/13 §6.2). */
export const KitchenTicketItemSchema = z
    .object({
    id: KitchenTicketItemIdSchema,
    name: z.string().min(1).max(120),
    quantity: z.int().min(1).max(999),
    modifiers: z.array(z.string().min(1).max(120)),
    note: z.string().min(1).max(2000).nullable(),
    sortOrder: z.int().min(0),
})
    .openapi('KitchenTicketItem');
/** A kitchen ticket (KOT): the order, the station it goes to, its items and its state (spec/13 §6.2). */
export const KitchenTicketSchema = z
    .object({
    id: KitchenTicketIdSchema,
    /**
     * The order the ticket is for. A counter or kiosk order is a **sale** (`saleId`); a ticket from the older
     * food-order foundation names its food order (`orderId`). Exactly one of the two is set.
     */
    orderId: FoodOrderIdSchema.nullable(),
    saleId: SaleIdSchema.nullable(),
    /** The order's human number for the venue's business day, shown "#0421" (F-FNB-04); null until allocated. */
    orderNumber: z.int().min(1).nullable(),
    /** Whether the order is paid (spec/13 §6.2 rule 3): a counter order may be made before it is paid. */
    paid: z.boolean(),
    venueId: z.string(),
    area: KitchenTicketAreaSchema,
    state: KitchenTicketStateSchema,
    /** The station the order is delivered to, or null for the counter (spec/13 §4, F-FNB-06). */
    stationId: StationIdSchema.nullable(),
    note: z.string().min(1).max(2000).nullable(),
    items: z.array(KitchenTicketItemSchema),
    placedAt: IsoDateTimeSchema,
    acceptedAt: IsoDateTimeSchema.nullable(),
    preparingAt: IsoDateTimeSchema.nullable(),
    readyAt: IsoDateTimeSchema.nullable(),
    deliveredAt: IsoDateTimeSchema.nullable(),
    updatedAt: IsoDateTimeSchema,
})
    .openapi('KitchenTicket');
export const KitchenTicketListSchema = z.object({ tickets: z.array(KitchenTicketSchema) }).openapi('KitchenTicketList');
const KitchenTicketQuerySchema = z
    .object({
    area: FoodPreparationAreaSchema.optional().openapi({ description: 'Only tickets for this preparation station.' }),
    /**
     * Only tickets in this state. With no filter the display shows the active tickets
     * (`new`, `accepted`, `preparing`, `ready`); `state=delivered` is the **recall** list — the last 10
     * tickets that left the board, most recent first (spec/13 §6.2 rule 4).
     */
    state: KitchenTicketStateSchema.optional(),
})
    .openapi('KitchenTicketQuery');
/** Advance a ticket to a new state, or recall it one step back (spec/13 §6.2). */
export const AdvanceKitchenTicketSchema = z
    .object({ state: KitchenTicketStateSchema })
    .openapi('AdvanceKitchenTicketRequest');
const KitchenTicketParamsSchema = VenueScopeParamsSchema.extend({ ticketId: KitchenTicketIdSchema }).openapi('KitchenTicketParams');
export const listKitchenTicketsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/kitchen/tickets',
    operationId: 'listKitchenTickets',
    tags: ['kitchen'],
    summary: 'The kitchen display: a venue’s tickets (F-FNB-04)',
    description: 'Active tickets by preparation station, oldest first; `state=delivered` is the recall list, the last 10 delivered. Requires `kitchen:read`.',
    [PERMISSION_EXTENSION]: KITCHEN_READ,
    request: { params: VenueScopeParamsSchema, query: KitchenTicketQuerySchema },
    responses: {
        200: jsonResponse(KitchenTicketListSchema, 'The venue’s kitchen tickets.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/**
 * Where a kitchen ticket routed when it was printed (spec/13 §11, F-FNB-05): to a printer at its preparation
 * station — a desk `device`'s or a `cloud` printer — or the `browser` when the station's printer is browser
 * printing, or the kitchen **display** as the fallback when no printer could take it (none configured, or offline
 * with no fallback). The same values as `PrintTarget`.
 */
export const KITCHEN_PRINT_TARGETS = ['browser', 'cloud', 'device', 'display'];
export const KitchenPrintTargetSchema = z
    .enum(KITCHEN_PRINT_TARGETS)
    .openapi('KitchenPrintTarget', { description: 'Where a kitchen ticket routed when printed (spec/13 §11).' });
/**
 * A kitchen ticket printed, with where it routed (F-FNB-05; spec/13 §6.3, §11). `job` is the `print_job` recorded for
 * it — its print document (the display's content with the order number in large type), its printer and its status —
 * so a reprint is a second job, never an edit of the first. The console prints `job.document` itself when `target` is
 * `browser`; with `display`, the ticket stays on the board and staff were alerted.
 */
export const KitchenTicketPrintJobSchema = z
    .object({
    ticket: KitchenTicketSchema,
    target: KitchenPrintTargetSchema,
    printedAt: IsoDateTimeSchema,
    job: PrintJobSchema,
})
    .openapi('KitchenTicketPrintJob');
export const advanceKitchenTicketRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/kitchen/tickets/{ticketId}/state',
    operationId: 'advanceKitchenTicket',
    tags: ['kitchen'],
    summary: 'Advance or recall a kitchen ticket (F-FNB-04)',
    description: 'Moves a ticket to the next state (accepted, preparing, ready, delivered) or recalls it one step back. ' +
        'Requires `kitchen:update`. Audited.',
    [PERMISSION_EXTENSION]: KITCHEN_UPDATE,
    request: {
        params: KitchenTicketParamsSchema,
        body: { content: { 'application/json': { schema: AdvanceKitchenTicketSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(KitchenTicketSchema, 'The ticket in its new state.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such ticket at this venue.', 'not-found'),
        409: problemResponse('That is not a legal transition from the ticket’s current state.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const printKitchenTicketRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/kitchen/tickets/{ticketId}/print',
    operationId: 'printKitchenTicket',
    tags: ['kitchen'],
    summary: 'Print or reprint a kitchen ticket (F-FNB-05)',
    description: 'Renders a ticket for its preparation station, records a print job and routes it (spec/13 §6.3, §11) — the ' +
        'station’s printer or its fallback, or the kitchen display when none can take it. Each call is a new job, so a ' +
        'reprint from the order or the display is a second job. Requires `kitchen:read`.',
    [PERMISSION_EXTENSION]: KITCHEN_READ,
    request: { params: KitchenTicketParamsSchema },
    responses: {
        200: jsonResponse(KitchenTicketPrintJobSchema, 'The rendered ticket and where it routed.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such ticket at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=kitchen.js.map